import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { getDegree } from './../api/EmpMasterApi';
import './GeneralInfoForm.css'; // Using the same CSS for consistent styling

const Education = forwardRef(({ formData, setFormData }, ref) => {
     // Add validation support
     const [validationErrors, setValidationErrors] = useState({});

     useImperativeHandle(ref, () => ({
          validateForm: () => {
               // Add validation logic here if needed
               return true; // For now, always return true
          },
          clearValidation: () => {
               setValidationErrors({});
          }
     }));

     // Initialize with one empty education row
     const [educations, setEducations] = useState(
          formData?.educations || [
               {
                    highest_qualification: '',
                    university: '',
                    passing_year: ''
               }
          ]
     );

     const [dropdowns, setDropdowns] = useState({
          degrees: []
     });
     const [loading, setLoading] = useState({
          degrees: true
     });
     // Fetch dropdown data immediately on mount
     useEffect(() => {
          console.log('🚀 Loading Education dropdowns...');
          // Fetch degrees immediately
          getDegree()
               .then(response => {
                    setDropdowns(prev => ({ ...prev, degrees: response?.data || [] }));
                    setLoading(prev => ({ ...prev, degrees: false }));
                    console.log('✅ Degrees loaded');
               })
               .catch(error => {
                    console.error('❌ Failed to load degrees:', error);
                    setLoading(prev => ({ ...prev, degrees: false }));
               });
     }, []); // Empty dependency array - run once on mount

     // Fetch dropdown data
     const fetchDropdowns = async () => {
          try {
               const degreesRes = await getDegree();
               setDropdowns({
                    degrees: degreesRes.data || []
               });
          } catch (error) {
               console.error('Error fetching dropdowns:', error);
          }
     };

     useEffect(() => {
          fetchDropdowns();
     }, []);

     // Add a new education row
     const addEducationRow = () => {
          const newEducation = {
               highest_qualification: '',
               university: '',
               passing_year: ''
          };
          const updatedEducations = [...educations, newEducation];
          setEducations(updatedEducations);

          // Update parent form data
          if (setFormData) {
               setFormData(prev => ({
                    ...prev,
                    educations: updatedEducations
               }));
          }
     };

     // Remove an education row
     const removeEducationRow = (index) => {
          if (educations.length > 1) {
               const updatedEducations = educations.filter((_, i) => i !== index);
               setEducations(updatedEducations);

               // Update parent form data
               if (setFormData) {
                    setFormData(prev => ({
                         ...prev,
                         educations: updatedEducations
                    }));
               }
          }
     };

     // Handle input changes
     const handleEducationChange = (index, field, value) => {
          const updatedEducations = educations.map((edu, i) =>
               i === index ? { ...edu, [field]: value } : edu
          );
          setEducations(updatedEducations);

          // Update parent form data
          if (setFormData) {
               setFormData(prev => ({
                    ...prev,
                    educations: updatedEducations
               }));
          }
     };

     return (
          <div className="custom_form_wrapper">
               {/* Table Header */}
               <div className="experience-table-header mb-2">
                    <div className="row gx-2 align-items-center">
                         <div className="col-4">
                              <small className="text-muted fw-semibold">Highest Qualification</small>
                         </div>
                         <div className="col-4">
                              <small className="text-muted fw-semibold">University</small>
                         </div>
                         <div className="col-2">
                              <small className="text-muted fw-semibold">Passing Year</small>
                         </div>
                         <div className="col-1">
                              <small className="text-muted fw-semibold">Action</small>
                         </div>
                         <div className="col-1 text-end">
                              <button
                                   type="button"
                                   className="btn btn-info btn-sm"
                                   onClick={addEducationRow}
                                   style={{ fontSize: '0.7rem', width: '30px', minWidth: '30px', padding: '4px 0' }}
                              >
                                   <i className="fas fa-plus"></i>
                              </button>
                         </div>
                    </div>
               </div>

               {/* Education Rows */}
               <div className="experience-table-body">
                    {educations.map((education, index) => (
                         <div key={index} className="experience-table-row mb-2">
                              <div className="row gx-2 align-items-center">
                                   {/* Highest Qualification */}
                                   <div className="col-4">
                                        <div className="form-floating">
                                             <select
                                                  className="form-select form-control-sm"
                                                  id={`highest_qualification_${index}`}
                                                  value={education.highest_qualification || ''}
                                                  onChange={(e) => handleEducationChange(index, 'highest_qualification', e.target.value)}
                                             >
                                                  <option value="" disabled hidden>
                                                       {loading.degrees ? 'Loading qualifications...' : 'Select Qualification'}
                                                  </option>
                                                  {/* Show current value first if it exists and not in dropdown yet */}
                                                  {education.highest_qualification && !dropdowns.degrees.find(deg => deg.degrees === education.highest_qualification) && (
                                                       <option value={education.highest_qualification}>
                                                            {education.highest_qualification}
                                                       </option>
                                                  )}
                                                  {Array.isArray(dropdowns.degrees) && dropdowns.degrees.map(deg => (
                                                       <option key={deg.id} value={deg.degrees}>
                                                            {deg.degrees}
                                                       </option>
                                                  ))}
                                             </select>
                                             <label htmlFor={`highest_qualification_${index}`} style={{ opacity: education.highest_qualification ? 1 : 0 }}>Highest Qualification</label>
                                        </div>
                                   </div>

                                   {/* University */}
                                   <div className="col-4">
                                        <div className="form-floating">
                                             <input
                                                  type="text"
                                                  className="form-control form-control-sm"
                                                  id={`university_${index}`}
                                                  placeholder="University"
                                                  value={education.university}
                                                  onChange={(e) => handleEducationChange(index, 'university', e.target.value)}
                                             />
                                             <label htmlFor={`university_${index}`}>University</label>
                                        </div>
                                   </div>

                                   {/* Passing Year */}
                                   <div className="col-2">
                                        <div className="form-floating">
                                             <input
                                                  type="number"
                                                  className="form-control form-control-sm"
                                                  id={`passing_year_${index}`}
                                                  placeholder="Passing Year"
                                                  value={education.passing_year}
                                                  onChange={(e) => handleEducationChange(index, 'passing_year', e.target.value)}
                                                  min="1950"
                                                  max="2030"
                                             />
                                             <label htmlFor={`passing_year_${index}`}>Passing Year</label>
                                        </div>
                                   </div>

                                   {/* Delete Button */}
                                   <div className="col-1 text-center">
                                        {educations.length > 1 && (
                                             <button
                                                  type="button"
                                                  className="btn btn-outline-danger btn-sm"
                                                  onClick={() => removeEducationRow(index)}
                                                  style={{
                                                       fontSize: '0.8rem',
                                                       width: '30px',
                                                       height: '30px',
                                                       minWidth: '30px',
                                                       minHeight: '30px',
                                                       padding: 0,
                                                       display: 'flex',
                                                       alignItems: 'center',
                                                       justifyContent: 'center'
                                                  }}
                                                  title="Remove Education"
                                             >
                                                  <i className="fas fa-trash"></i>
                                             </button>
                                        )}
                                   </div>
                              </div>
                         </div>
                    ))}
               </div>

               {educations.length === 0 && (
                    <div className="text-center py-4">
                         <p className="text-muted">No education added yet.</p>
                         <button
                              type="button"
                              className="btn btn-primary"
                              onClick={addEducationRow}
                         >
                              <i className="fas fa-plus me-2"></i>
                              Add First Education
                         </button>
                    </div>
               )}
          </div>
     );
});

export default Education;